import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    ButtonGroup,
    Alert,
    Tooltip,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
    Download as DownloadIcon,
    TableChart as TableIcon,
    Assessment as AssessmentIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const TablaIndicadoresMaleza = ({
    lotes_data = [],
    areaMalezaTotal,
    areaPoligonoTotal,
    porcentajeMalezaTotal,
    shapefileUrl,
    disableShapefile = false,
}) => {
    const theme = useTheme();
    const [sortedData, setSortedData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lote_id', direction: 'asc' });

    const numberFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    /* ── Sorting ── */
    useEffect(() => {
        if (lotes_data && Array.isArray(lotes_data)) {
            const sorted = [...lotes_data].sort((a, b) => {
                const { key, direction } = sortConfig;
                if (key === 'lote_id') {
                    const aNum = parseInt(a[key]) || 0;
                    const bNum = parseInt(b[key]) || 0;
                    return direction === 'asc' ? aNum - bNum : bNum - aNum;
                } else if (['codigo_lote', 'id_numerico', 'productor', 'zona'].includes(key)) {
                    const aValue = (a[key] != null) ? a[key].toString().toLowerCase() : '';
                    const bValue = (b[key] != null) ? b[key].toString().toLowerCase() : '';
                    return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                } else {
                    if (key === 'total_usd') {
                        const aTotal = (Number(a.area_maleza_ha) || 0) * (Number(a.precio_usd_ha) || 0);
                        const bTotal = (Number(b.area_maleza_ha) || 0) * (Number(b.precio_usd_ha) || 0);
                        return direction === 'asc' ? aTotal - bTotal : bTotal - aTotal;
                    }
                    const aVal = a[key] != null ? Number(a[key]) : 0;
                    const bVal = b[key] != null ? Number(b[key]) : 0;
                    return direction === 'asc' ? aVal - bVal : bVal - aVal;
                }
            });
            setSortedData(sorted);
        }
    }, [lotes_data, sortConfig]);

    /* ── Filtering ── */
    useEffect(() => {
        let filtered = [...sortedData];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(lote =>
                lote.lote_id.toLowerCase().includes(term) ||
                lote.codigo_lote.toLowerCase().includes(term) ||
                lote.id_numerico.toString().toLowerCase().includes(term) ||
                (lote.productor && lote.productor.toLowerCase().includes(term)) ||
                (lote.zona && lote.zona.toLowerCase().includes(term))
            );
        }
        setFilteredData(filtered);
    }, [sortedData, searchTerm]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSortConfig({ key: 'lote_id', direction: 'asc' });
    };

    /* ── CSV Export ── */
    const exportToCSV = () => {
        const dataToExport = filteredData.length > 0 ? filteredData : sortedData;
        if (!dataToExport || dataToExport.length === 0) return;

        const headers = [
            'Finca', 'Productor', 'ID', 'Zona', 'Lote', 'Código',
            'Área Total del bloque (ha)', 'Área con Maleza (ha)',
            'Porcentaje de Maleza (%)', 'Costo (USD/ha)', 'Total (USD)',
        ];

        const csvEscape = (val) => {
            if (val === null || val === undefined) return '';
            const s = String(val);
            if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
            return s;
        };

        const rows = [];
        rows.push(headers.map(h => csvEscape(h)));
        dataToExport.forEach(lote => {
            const precio = lote.precio_usd_ha != null ? numberFormatter.format(Number(lote.precio_usd_ha)) : '';
            const total = (lote.area_maleza_ha && lote.precio_usd_ha)
                ? numberFormatter.format(Number(lote.area_maleza_ha) * Number(lote.precio_usd_ha)) : '';
            rows.push([
                csvEscape(lote.nombre_finca), csvEscape(lote.productor),
                csvEscape(lote.id_numerico), csvEscape(lote.zona),
                csvEscape(lote.lote_id), csvEscape(lote.codigo_lote),
                csvEscape(lote.area_lote_ha), csvEscape(lote.area_maleza_ha),
                csvEscape(lote.porcentaje_maleza), csvEscape(precio), csvEscape(total),
            ]);
        });
        rows.push([]);
        rows.push([
            csvEscape(`${dataToExport.length} lotes`), '', csvEscape('TOTALES'), '', '',
            csvEscape(areaTotalCalculada()), csvEscape(areaMalezaTotalCalculada()),
            csvEscape(porcentajeMalezaTotalCalculado()), '',
            csvEscape(numberFormatter.format(Number(totalCostoCalculado()))),
        ]);

        const csvContent = rows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `deteccion_maleza_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /* ── Excel Export (themed) ── */
    const exportToExcel = async () => {
        const dataToExport = filteredData.length > 0 ? filteredData : sortedData;
        if (!dataToExport || dataToExport.length === 0) return;

        try {
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Geomotica';
            workbook.created = new Date();

            const sheet = workbook.addWorksheet('Detección Maleza');

            // Title — use theme primary (#ff9900)
            sheet.mergeCells('A1:K1');
            const titleCell = sheet.getCell('A1');
            titleCell.value = 'REPORTE DE DETECCIÓN DE MALEZA';
            titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2d3748' } };

            // Date
            sheet.getCell('A2').value = 'Fecha:';
            sheet.getCell('B2').value = new Date().toLocaleDateString();

            // Headers
            const headerRowValues = [
                'Finca', 'Productor', 'ID', 'Zona', 'Lote', 'Código',
                'Área Total del bloque (ha)', 'Área con Maleza (ha)',
                'Porcentaje de Maleza (%)', 'Costo (USD/ha)', 'Total (USD)',
            ];
            sheet.addRow([]);
            sheet.addRow([]);
            const header = sheet.addRow(headerRowValues);

            header.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2d3748' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            // Data rows — alternating #fff3e0 (theme primary light)
            dataToExport.forEach((lote) => {
                const precio = lote.precio_usd_ha != null ? Number(lote.precio_usd_ha) : null;
                const total = (lote.area_maleza_ha && precio) ? Number(lote.area_maleza_ha) * precio : 0;
                const row = sheet.addRow([
                    lote.nombre_finca, lote.productor, lote.id_numerico, lote.zona,
                    lote.lote_id, lote.codigo_lote, Number(lote.area_lote_ha),
                    Number(lote.area_maleza_ha), Number(lote.porcentaje_maleza),
                    precio, Number(total),
                ]);

                row.getCell(7).numFmt = '#,##0.0000';
                row.getCell(8).numFmt = '#,##0.0000';
                row.getCell(9).numFmt = '0.00';
                row.getCell(10).numFmt = '#,##0.00';
                row.getCell(11).numFmt = '#,##0.00';

                row.eachCell((cell) => {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                });

                try {
                    const isEven = (row.number % 2) === 0;
                    const evenFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfff3e0' } };
                    const oddFill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
                    row.eachCell((cell) => { cell.fill = isEven ? evenFill : oddFill; });
                    row.height = 18;
                } catch (rErr) { console.warn('row styling skipped', rErr); }
            });

            // AutoFilter
            try {
                const firstCol = 1;
                const lastCol = 11;
                const headerRowIndex = header.number;
                const lastDataRowIndex = sheet.lastRow.number;
                sheet.autoFilter = {
                    from: { row: headerRowIndex, column: firstCol },
                    to:   { row: lastDataRowIndex, column: lastCol },
                };
            } catch (fErr) { console.warn('autoFilter not applied:', fErr); }

            // Totals
            sheet.addRow([]);
            const firstDataRow = header.number + 1;
            const lastDataRow = sheet.lastRow.number;
            const totalsRow = sheet.addRow([]);
            totalsRow.getCell(1).value = { formula: `SUBTOTAL(103,E${firstDataRow}:E${lastDataRow})&" lotes"` };
            totalsRow.getCell(3).value = 'TOTALES';
            totalsRow.getCell(7).value  = { formula: `SUBTOTAL(109,G${firstDataRow}:G${lastDataRow})` };
            totalsRow.getCell(8).value  = { formula: `SUBTOTAL(109,H${firstDataRow}:H${lastDataRow})` };
            totalsRow.getCell(9).value  = { formula: `IF(SUBTOTAL(109,G${firstDataRow}:G${lastDataRow})=0,0,(SUBTOTAL(109,H${firstDataRow}:H${lastDataRow})/SUBTOTAL(109,G${firstDataRow}:G${lastDataRow}))*100)` };
            totalsRow.getCell(11).value = { formula: `SUBTOTAL(109,K${firstDataRow}:K${lastDataRow})` };

            totalsRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfff3e0' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
            totalsRow.getCell(7).numFmt = '#,##0.0000';
            totalsRow.getCell(8).numFmt = '#,##0.0000';
            totalsRow.getCell(9).numFmt = '0.00';
            totalsRow.getCell(11).numFmt = '#,##0.00';

            // Auto-width
            try {
                const colMax = new Array(headerRowValues.length).fill(0);
                headerRowValues.forEach((h, i) => { colMax[i] = Math.max(colMax[i], String(h || '').length); });
                dataToExport.forEach((lote) => {
                    const vals = [
                        lote.nombre_finca || '', lote.productor || '', String(lote.id_numerico || ''),
                        lote.zona || '', lote.lote_id || '', lote.codigo_lote || '',
                        lote.area_lote_ha != null ? Number(lote.area_lote_ha).toFixed(4) : '',
                        lote.area_maleza_ha != null ? Number(lote.area_maleza_ha).toFixed(4) : '',
                        lote.porcentaje_maleza != null ? Number(lote.porcentaje_maleza).toFixed(2) + '%' : '',
                        lote.precio_usd_ha != null ? numberFormatter.format(Number(lote.precio_usd_ha)) : '',
                        (lote.area_maleza_ha && lote.precio_usd_ha)
                            ? numberFormatter.format(Number(lote.area_maleza_ha) * Number(lote.precio_usd_ha))
                            : numberFormatter.format(0),
                    ];
                    vals.forEach((v, i) => { colMax[i] = Math.max(colMax[i], String(v || '').length); });
                });
                const extraBuffer = 4;
                colMax.forEach((maxLen, idx) => {
                    sheet.getColumn(idx + 1).width = Math.min(Math.max(maxLen + 2 + extraBuffer, 8), 50);
                });
            } catch (wErr) {
                const widths = [20, 18, 10, 18, 12, 12, 18, 18, 16, 16, 16];
                widths.forEach((w, i) => sheet.getColumn(i + 1).width = w);
            }

            sheet.views = [{ state: 'frozen', ySplit: header.number }];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `deteccion_maleza_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            console.error('Error exporting Excel:', err);
            toast.error('Error al generar el archivo Excel.', { position: 'top-right', autoClose: 5000 });
        }
    };

    /* ── Shapefile download ── */
    const downloadShapefile = () => {
        const urlToUse = shapefileUrl || localStorage.getItem('deteccionMaleza_shapefileUrl');
        if (!urlToUse) {
            toast.warning('No hay shapefile disponible para descargar.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        toast.info('Descargando shapefile...', { position: 'top-right', autoClose: 3000 });
        try {
            fetch(urlToUse, { method: 'HEAD' })
                .then(response => {
                    if (response.ok) { window.open(urlToUse, '_blank'); }
                    else { throw new Error(`Error HTTP: ${response.status}`); }
                })
                .catch(() => {
                    toast.error('Error al descargar el shapefile. La URL puede haber expirado.', { position: 'top-right', autoClose: 5000 });
                });
        } catch {
            toast.error('Error al descargar el shapefile.', { position: 'top-right', autoClose: 5000 });
        }
    };

    /* ── Calculations ── */
    const dataForCalc = filteredData.length > 0 ? filteredData : sortedData;
    const areaTotalCalculada         = () => dataForCalc.reduce((s, l) => s + l.area_lote_ha, 0).toFixed(4);
    const areaMalezaTotalCalculada   = () => dataForCalc.reduce((s, l) => s + l.area_maleza_ha, 0).toFixed(4);
    const porcentajeMalezaTotalCalculado = () => {
        const at = dataForCalc.reduce((s, l) => s + l.area_lote_ha, 0);
        const am = dataForCalc.reduce((s, l) => s + l.area_maleza_ha, 0);
        return at > 0 ? ((am / at) * 100).toFixed(2) : '0.00';
    };
    const totalCostoCalculado = () => dataForCalc.reduce((s, l) => {
        const p = l.precio_usd_ha != null ? Number(l.precio_usd_ha) : 0;
        return s + (p * (l.area_maleza_ha ? Number(l.area_maleza_ha) : 0));
    }, 0).toFixed(2);

    /* ── Theme-derived tokens ── */
    const primary      = theme.palette.primary.main;      // #ff9900
    const primaryLight = theme.palette.primary.light;      // #fff3e0
    const bgElevated   = theme.palette.surface?.elevated || '#F1F5F9';
    const borderRadius = theme.shape.borderRadius * 1.2;   // ~12px

    if (!sortedData || sortedData.length === 0) {
        return <Alert severity="info" sx={{ mt: 2 }}>No hay datos de lotes disponibles para mostrar.</Alert>;
    }

    const displayData = filteredData.length > 0 ? filteredData : sortedData;

    /* ── Sort header helper ── */
    const SortableHeader = ({ label, field }) => (
        <TableCell align="center" sx={{
            position: 'sticky', top: 0, zIndex: 2,
            backgroundColor: primary, color: '#fff', whiteSpace: 'nowrap',
        }}>
            <Button
                variant="text" size="small"
                onClick={() => handleSort(field)}
                startIcon={sortConfig.key === field
                    ? (sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)
                    : null}
                sx={{ fontWeight: 700, color: '#fff', fontSize: '0.75rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
                {label}
            </Button>
        </TableCell>
    );

    return (
        <Box sx={{ mt: 3 }}>
            {/* ── Toolbar ── */}
            <Box sx={{
                mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 1,
            }}>
                <Typography variant="h6" component="h2" fontWeight={600}>
                    Indicadores de Detección de Maleza por Lote
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small" placeholder="Buscar..."
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: '200px' }}
                    />
                    <ButtonGroup size="small" variant="outlined">
                        <Tooltip title="Exportar a Excel">
                            <Button onClick={exportToExcel} startIcon={<AssessmentIcon />} color="primary">Excel</Button>
                        </Tooltip>
                        <Tooltip title="Exportar a CSV">
                            <Button onClick={exportToCSV} startIcon={<TableIcon />} color="secondary">CSV</Button>
                        </Tooltip>
                        <Tooltip title={disableShapefile ? 'Descarga deshabilitada' : ((!shapefileUrl && !localStorage.getItem('deteccionMaleza_shapefileUrl')) ? 'No hay shapefile disponible' : 'Descargar Shapefile')}>
                            <span>
                                <Button onClick={downloadShapefile} startIcon={<DownloadIcon />} color="success"
                                    disabled={disableShapefile || (!shapefileUrl && !localStorage.getItem('deteccionMaleza_shapefileUrl'))}>
                                    Shapefile
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Limpiar filtros">
                            <Button onClick={clearFilters} startIcon={<FilterListIcon />}>Limpiar</Button>
                        </Tooltip>
                    </ButtonGroup>
                </Box>
            </Box>

            {/* ── Table ── */}
            <TableContainer component={Paper} elevation={2}
                sx={{ borderRadius: `${borderRadius}px`, overflow: 'auto', maxHeight: '70vh' }}>
                <Table size="small" stickyHeader aria-label="tabla de detección de maleza por lotes">
                    <TableHead>
                        <TableRow>
                            <SortableHeader label="Finca"             field="nombre_finca" />
                            <SortableHeader label="Productor"         field="productor" />
                            <SortableHeader label="ID"                field="id_numerico" />
                            <SortableHeader label="Zona"              field="zona" />
                            <SortableHeader label="Lote"              field="lote_id" />
                            <SortableHeader label="Código"            field="codigo_lote" />
                            <SortableHeader label="Área Total (ha)"   field="area_lote_ha" />
                            <SortableHeader label="Área Maleza (ha)"  field="area_maleza_ha" />
                            <SortableHeader label="% Maleza"          field="porcentaje_maleza" />
                            <SortableHeader label="Costo (USD/ha)"    field="precio_usd_ha" />
                            <SortableHeader label="Total (USD)"       field="total_usd" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayData.map((lote, index) => (
                            <TableRow
                                key={`${lote.lote_id}-${index}`}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? '#fff' : bgElevated,
                                    '&:hover': { backgroundColor: primaryLight },
                                    transition: 'background-color 0.15s',
                                }}
                            >
                                <TableCell align="center"><Typography variant="body2">{lote.nombre_finca}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2">{lote.productor}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace">{lote.id_numerico}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2">{lote.zona}</Typography></TableCell>
                                <TableCell align="center" component="th" scope="row"><Typography variant="body2" fontWeight="medium">{lote.lote_id}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace">{lote.codigo_lote}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace">{lote.area_lote_ha.toFixed(4)}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace">{lote.area_maleza_ha.toFixed(4)}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{lote.porcentaje_maleza.toFixed(2)}%</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace">{lote.precio_usd_ha != null ? numberFormatter.format(Number(lote.precio_usd_ha)) : '-'}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{(lote.area_maleza_ha && lote.precio_usd_ha) ? numberFormatter.format(Number(lote.area_maleza_ha) * Number(lote.precio_usd_ha)) : numberFormatter.format(0)}</Typography></TableCell>
                            </TableRow>
                        ))}
                        {/* Totals row */}
                        <TableRow sx={{
                            backgroundColor: primaryLight,
                            borderTop: `2px solid ${primary}`,
                            position: 'sticky', bottom: 0, zIndex: 1,
                        }}>
                            <TableCell align="center"><Typography variant="body2" fontWeight="bold">{displayData.length} lote{displayData.length !== 1 ? 's' : ''}</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontWeight="bold">-</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontWeight="bold">-</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontWeight="bold">-</Typography></TableCell>
                            <TableCell align="center" component="th" scope="row"><Typography variant="body2" fontWeight="bold">TOTALES</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontWeight="bold">-</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{areaTotalCalculada()}</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{areaMalezaTotalCalculada()}</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{porcentajeMalezaTotalCalculado()}%</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">-</Typography></TableCell>
                            <TableCell align="center"><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{totalCostoCalculado()}</Typography></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── Summary ── */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: bgElevated, borderRadius: `${borderRadius}px` }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>Resumen:</strong>{' '}
                    {filteredData.length > 0 && filteredData.length !== sortedData.length
                        ? `Se muestran ${filteredData.length} de ${sortedData.length} lotes (filtrados)`
                        : `Se analizaron ${sortedData.length} lote${sortedData.length !== 1 ? 's' : ''}`
                    } con un área total de {areaTotalCalculada()} hectáreas.
                    El porcentaje promedio de maleza es {porcentajeMalezaTotalCalculado()}%.
                </Typography>
            </Box>
        </Box>
    );
};

export default TablaIndicadoresMaleza;
